import { useQuery } from "@tanstack/react-query";
import type { AreasListResponse } from "@situate/contracts";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const AREAS_LIST_PATH = "/v1/areas";

/**
 * Fetches the current deployment's available areas from the runtime API,
 * mapped to the `AreaLocation` shape used by deployment area filters.
 *
 * Disabled until a runtime session client exists for the deployment and the
 * `areas_list` operation has been granted; re-fetches automatically whenever
 * the runtime session (and therefore `deploymentId`) changes.
 */
export function useAreas(deploymentId: string) {
  const runtime = useRuntimeHttpClient();
  const hasAreasListGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "areas_list"
  );
  const client = runtime.client;

  return useQuery<AreaLocation[]>({
    queryKey: ["areas", deploymentId],
    queryFn: async () => {
      const response = await client!.get<AreasListResponse>(AREAS_LIST_PATH);
      return response.areas.map((area) => ({
        id: area.area_id,
        name: area.area_display_name,
        derivedPopulationMetrics: area.derived_population_metrics,
      }));
    },
    enabled: Boolean(client) && runtime.deploymentId === deploymentId && hasAreasListGrant,
  });
}
